// src/pages/planejamento/CalendarioAgendamentosPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";

import LayoutDesktop from "../../layouts/LayoutDesktop";
import LayoutMobile from "../../layouts/LayoutMobile";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useIsMobile } from "../../hooks/useIsMobile";
import { usePlanejamentoAtendimentos } from "../../hooks/usePlanejamentoAtendimentos";
import { NIVEIS } from "../../config/niveis";
import { nivelUsuario } from "../../utils/permissoes";
import { db } from "../../config/firebase";

const DIAS_SEMANA = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sab"];
const MESES = [
  "Janeiro", "Fevereiro", "Marco", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

function toDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

function formatDateTime(value) {
  const dt = toDate(value);
  if (!dt) return "-";
  return dt.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function hexToRgba(hex, alpha) {
  if (!hex) return `rgba(59, 130, 246, ${alpha})`;
  const clean = String(hex).replace("#", "");
  if (clean.length !== 6) return `rgba(59, 130, 246, ${alpha})`;
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return `rgba(59, 130, 246, ${alpha})`;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function startOfMonth(year, month) {
  const d = new Date(year, month, 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getDiasDoMes(ano, mes) {
  const primeiroDia = new Date(ano, mes, 1);
  const ultimoDia = new Date(ano, mes + 1, 0);
  const diasNoMes = ultimoDia.getDate();
  const diaSemanaInicio = primeiroDia.getDay();

  const dias = [];
  for (let i = 0; i < diaSemanaInicio; i += 1) {
    dias.push(null);
  }

  for (let dia = 1; dia <= diasNoMes; dia += 1) {
    dias.push(dia);
  }

  return dias;
}

const JANELA_HORARIO_INICIO_MIN = 6 * 60;
const JANELA_HORARIO_FIM_MIN = 18 * 60;
const JANELA_HORARIO_TOTAL_MIN = JANELA_HORARIO_FIM_MIN - JANELA_HORARIO_INICIO_MIN;

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function minutosDoDia(date) {
  if (!date) return null;
  return date.getHours() * 60 + date.getMinutes();
}

function calcularFaixaPill(inicio, fim) {
  if (!inicio) {
    return { startPct: 0, endPct: 100, allDay: true };
  }

  const inicioMin = minutosDoDia(inicio);
  if (inicioMin == null) {
    return { startPct: 0, endPct: 100, allDay: true };
  }

  let fimMin = minutosDoDia(fim);
  if (fimMin == null) fimMin = inicioMin + 60;
  if (fimMin <= inicioMin) fimMin = inicioMin + 60;

  const cobreJanelaToda = inicioMin <= JANELA_HORARIO_INICIO_MIN && fimMin >= JANELA_HORARIO_FIM_MIN;
  const duracao = fimMin - inicioMin;
  const allDay = cobreJanelaToda || duracao >= JANELA_HORARIO_TOTAL_MIN;

  if (allDay) {
    return { startPct: 0, endPct: 100, allDay: true };
  }

  const inicioClamp = clamp(inicioMin, JANELA_HORARIO_INICIO_MIN, JANELA_HORARIO_FIM_MIN);
  const fimClamp = clamp(fimMin, JANELA_HORARIO_INICIO_MIN, JANELA_HORARIO_FIM_MIN);

  let startPct = ((inicioClamp - JANELA_HORARIO_INICIO_MIN) / JANELA_HORARIO_TOTAL_MIN) * 100;
  let endPct = ((fimClamp - JANELA_HORARIO_INICIO_MIN) / JANELA_HORARIO_TOTAL_MIN) * 100;

  if (endPct <= startPct) {
    endPct = Math.min(100, startPct + (60 / JANELA_HORARIO_TOTAL_MIN) * 100);
  }

  return {
    startPct: clamp(startPct, 0, 100),
    endPct: clamp(endPct, 0, 100),
    allDay: false,
  };
}

function calcularFaixaPillNoDia(inicio, fim, diaRef) {
  if (!diaRef) return calcularFaixaPill(inicio, fim);

  const inicioBase = inicio || null;
  const fimBase = fim || (inicioBase ? new Date(inicioBase.getTime() + 60 * 60 * 1000) : null);
  if (!inicioBase) return { startPct: 0, endPct: 100, allDay: true };

  const diaInicio = startOfDay(diaRef);
  const diaFim = endOfDay(diaRef);

  const segInicio = inicioBase > diaInicio ? inicioBase : diaInicio;
  const segFim = fimBase < diaFim ? fimBase : diaFim;

  if (segFim <= segInicio) {
    const fimAjustado = new Date(segInicio.getTime() + 60 * 60 * 1000);
    return calcularFaixaPill(segInicio, fimAjustado);
  }

  return calcularFaixaPill(segInicio, segFim);
}

function slotsFromFaixaPill(faixaPill) {
  if (!faixaPill) return [];
  if (faixaPill.allDay) return [0, 1, 2, 3];

  const startPct = Number(faixaPill.startPct ?? 0);
  const endPct = Number(faixaPill.endPct ?? 0);
  if (Number.isNaN(startPct) || Number.isNaN(endPct)) return [];

  const inicioSlot = clamp(Math.floor(startPct / 25), 0, 3);
  const fimSlot = clamp(Math.ceil(endPct / 25) - 1, 0, 3);
  if (fimSlot < inicioSlot) return [inicioSlot];

  const slots = [];
  for (let slot = inicioSlot; slot <= fimSlot; slot += 1) {
    slots.push(slot);
  }
  return slots;
}

export default function CalendarioAgendamentosPage() {
  const { tema, alterarTema } = useTheme();
  const { usuario } = useAuth();
  const isMobile = useIsMobile(900);

  const { scope, listarSessoesPorPeriodo } = usePlanejamentoAtendimentos();

  const [sidebarAberta, setSidebarAberta] = useState(false);
  const [tecnicos, setTecnicos] = useState([]);
  const [clientes, setClientes] = useState([]);

  const [tecnicoId, setTecnicoId] = useState("");
  const [sessoes, setSessoes] = useState([]);
  const [atendimentos, setAtendimentos] = useState([]);

  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [textoFiltro, setTextoFiltro] = useState("");

  const [abaAtiva, setAbaAtiva] = useState("calendario");
  const [mesAtual, setMesAtual] = useState(new Date().getMonth());
  const [anoAtual, setAnoAtual] = useState(new Date().getFullYear());
  const [diaSelecionado, setDiaSelecionado] = useState(null);

  const canGlobalView = useMemo(() => {
    const nivel = nivelUsuario(usuario);
    return (
      usuario?.acessoGlobal === true
      || usuario?.globalAccess === true
      || usuario?.tipoAcesso === "GLOBAL"
      || nivel >= NIVEIS.ADMIN
    );
  }, [usuario]);

  const isGlobalTecnico = tecnicoId === "__GLOBAL__";

  useEffect(() => {
    const loadClientes = async () => {
      if (!usuario?.grupoId) return;
      const q = query(collection(db, "empresas"), where("grupoId", "==", usuario.grupoId));
      const snap = await getDocs(q);
      const lista = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR", { sensitivity: "base" }));
      setClientes(lista);
    };
    loadClientes();
  }, [usuario?.grupoId]);

  useEffect(() => {
    const loadTecnicos = async () => {
      if (!usuario?.grupoId) return;
      const q = query(collection(db, "usuarios"), where("grupoId", "==", usuario.grupoId));
      const snap = await getDocs(q);
      const lista = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((u) => nivelUsuario(u) >= NIVEIS.TECNICO)
        .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR", { sensitivity: "base" }));
      setTecnicos(lista);
      if (!tecnicoId && lista.length) {
        setTecnicoId(lista[0].id);
      }
    };
    loadTecnicos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario?.grupoId]);

  const clientesById = useMemo(() => {
    const map = {};
    clientes.forEach((c) => {
      map[String(c.id)] = c;
    });
    return map;
  }, [clientes]);

  const tecnicosById = useMemo(() => {
    const map = {};
    tecnicos.forEach((t) => {
      map[String(t.id)] = t;
    });
    return map;
  }, [tecnicos]);

  const atendimentosById = useMemo(() => {
    const map = {};
    atendimentos.forEach((a) => {
      map[String(a.id)] = a;
    });
    return map;
  }, [atendimentos]);

  useEffect(() => {
    const carregarSessoes = async () => {
      if (!tecnicoId) {
        setSessoes([]);
        return;
      }
      if (!scope?.grupoId) return;
      if (!scope?.empresaId && !canGlobalView) {
        setErro("Selecione uma empresa para carregar o calendario.");
        setSessoes([]);
        return;
      }
      if (isGlobalTecnico && !canGlobalView) {
        setErro("Acesso global nao autorizado.");
        setSessoes([]);
        return;
      }

      setErro("");
      setCarregando(true);
      try {
        const inicio = startOfMonth(anoAtual, mesAtual);
        const fim = startOfMonth(anoAtual, mesAtual + 1);
        const lista = await listarSessoesPorPeriodo(inicio, fim, {
          empresaId: scope?.empresaId || null,
          tecnicoId: isGlobalTecnico ? null : tecnicoId,
        });
        setSessoes(lista || []);
      } catch (e) {
        console.error("Erro ao carregar calendario:", e);
        setErro("Erro ao carregar calendario. Tente novamente.");
        setSessoes([]);
      } finally {
        setCarregando(false);
      }
    };

    carregarSessoes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tecnicoId, isGlobalTecnico, mesAtual, anoAtual, scope?.grupoId, scope?.empresaId, canGlobalView]);

  useEffect(() => {
    const carregarAtendimentos = async () => {
      const ids = Array.from(new Set(sessoes.map((s) => String(s.atendimentoId || "")))).filter(Boolean);
      const missing = ids.filter((id) => !atendimentosById[id]);
      if (!missing.length) return;
      const fetched = await Promise.all(
        missing.map(async (id) => {
          try {
            const ref = doc(db, "atendimentos_preventiva", id);
            const snap = await getDoc(ref);
            if (snap.exists()) return { id: snap.id, ...snap.data() };
          } catch (e) {
            console.warn("Falha ao carregar atendimento:", e);
          }
          return null;
        })
      );
      const adicionais = fetched.filter(Boolean);
      if (adicionais.length) {
        setAtendimentos((prev) => {
          const map = new Map(prev.map((a) => [a.id, a]));
          adicionais.forEach((a) => map.set(a.id, a));
          return Array.from(map.values());
        });
      }
    };

    if (sessoes.length) {
      carregarAtendimentos();
    }
  }, [sessoes, atendimentosById]);

  const sessoesComMeta = useMemo(() => {
    return sessoes.map((s) => {
      const atendimento = atendimentosById[s.atendimentoId] || {};
      const clienteNome = clientesById[atendimento.clienteId]?.nome || "-";
      const tecnicoNome = tecnicosById[s.tecnicoId]?.nome || "-";
      const tecnicoCor = tecnicosById[s.tecnicoId]?.corAgenda || "#3b82f6";
      const inicioEm = toDate(s.inicioEm);
      const fimEm = toDate(s.fimEm);
      return {
        ...s,
        atendimento,
        clienteNome,
        tecnicoNome,
        tecnicoCor,
        inicioEm,
        fimEm,
      };
    });
  }, [sessoes, atendimentosById, clientesById, tecnicosById]);

  const sessoesFiltradas = useMemo(() => {
    const texto = textoFiltro.trim().toLowerCase();
    if (!texto) return sessoesComMeta;
    return sessoesComMeta.filter((s) => {
      const campos = [
        s.clienteNome,
        s.tecnicoNome,
        s.atendimento?.competencia,
        s.atendimento?.contratoTipo,
        s.status,
      ];
      return campos.some((campo) => String(campo || "").toLowerCase().includes(texto));
    });
  }, [sessoesComMeta, textoFiltro]);

  const sessoesPorDia = useMemo(() => {
    const mapa = {};
    sessoesComMeta.forEach((s) => {
      if (!s.inicioEm) return;

      const inicio = s.inicioEm;
      const fim = s.fimEm || new Date(inicio.getTime() + 60 * 60 * 1000);
      const inicioDia = startOfDay(inicio);
      const fimDia = startOfDay(fim);

      const cursor = new Date(inicioDia);
      while (cursor <= fimDia) {
        const dataKey = `${cursor.getFullYear()}-${cursor.getMonth()}-${cursor.getDate()}`;
        if (!mapa[dataKey]) mapa[dataKey] = [];

        mapa[dataKey].push({
          ...s,
          faixaPill: calcularFaixaPillNoDia(inicio, fim, cursor),
          multiDia: inicioDia.getTime() !== fimDia.getTime(),
        });

        cursor.setDate(cursor.getDate() + 1);
        cursor.setHours(0, 0, 0, 0);
      }
    });

    Object.keys(mapa).forEach((key) => {
      mapa[key].sort((a, b) => {
        const aAllDay = a?.faixaPill?.allDay ? 1 : 0;
        const bAllDay = b?.faixaPill?.allDay ? 1 : 0;
        if (aAllDay !== bAllDay) return bAllDay - aAllDay;
        const aInicio = a?.inicioEm ? a.inicioEm.getTime() : 0;
        const bInicio = b?.inicioEm ? b.inicioEm.getTime() : 0;
        return aInicio - bInicio;
      });
    });

    return mapa;
  }, [sessoesComMeta]);

  const pillsPorDia = useMemo(() => {
    const mapa = {};

    Object.entries(sessoesPorDia).forEach(([dataKey, sessoesDia]) => {
      const porTecnico = new Map();

      (sessoesDia || []).forEach((sessao) => {
        const slotsSessao = slotsFromFaixaPill(sessao?.faixaPill);
        const principalId = String(sessao?.tecnicoId || "").trim();
        const acompanhantesIds = Array.isArray(sessao?.tecnicosAcompanhantesIds)
          ? sessao.tecnicosAcompanhantesIds.map((id) => String(id || "").trim()).filter(Boolean)
          : [];

        const participantesIds = Array.from(new Set([principalId, ...acompanhantesIds].filter(Boolean)));
        participantesIds.forEach((participanteId) => {
          const tecnico = tecnicosById[participanteId] || {};
          const corPadraoSessao = participanteId === principalId
            ? (sessao?.tecnicoCor || "#3b82f6")
            : "#3b82f6";
          const cor = tecnico?.corAgenda || corPadraoSessao;
          const nome = tecnico?.nome || tecnico?.email || (participanteId === principalId ? sessao?.tecnicoNome : "Técnico") || "Técnico";

          if (!porTecnico.has(participanteId)) {
            porTecnico.set(participanteId, {
              tecnicoId: participanteId,
              tecnicoNome: nome,
              tecnicoCor: cor,
              slots: new Set(),
              totalServicos: 0,
            });
          }

          const pill = porTecnico.get(participanteId);
          pill.totalServicos += 1;
          slotsSessao.forEach((slot) => pill.slots.add(slot));
        });
      });

      const lista = Array.from(porTecnico.values())
        .map((pill) => ({
          ...pill,
          slots: [0, 1, 2, 3].map((slot) => pill.slots.has(slot)),
          preenchidos: Number([0, 1, 2, 3].filter((slot) => pill.slots.has(slot)).length),
        }))
        .sort((a, b) => {
          if (b.preenchidos !== a.preenchidos) return b.preenchidos - a.preenchidos;
          if (b.totalServicos !== a.totalServicos) return b.totalServicos - a.totalServicos;
          return String(a.tecnicoNome || "").localeCompare(String(b.tecnicoNome || ""), "pt-BR", { sensitivity: "base" });
        });

      mapa[dataKey] = lista;
    });

    return mapa;
  }, [sessoesPorDia, tecnicosById]);

  function navegarMes(direcao) {
    let novoMes = mesAtual + direcao;
    let novoAno = anoAtual;

    if (novoMes < 0) {
      novoMes = 11;
      novoAno -= 1;
    } else if (novoMes > 11) {
      novoMes = 0;
      novoAno += 1;
    }

    setMesAtual(novoMes);
    setAnoAtual(novoAno);
    setDiaSelecionado(null);
  }

  function irParaHoje() {
    const hoje = new Date();
    setMesAtual(hoje.getMonth());
    setAnoAtual(hoje.getFullYear());
    setDiaSelecionado(hoje.getDate());
  }

  function getSessoesDoDia(dia) {
    if (!dia) return [];
    const dataKey = `${anoAtual}-${mesAtual}-${dia}`;
    return sessoesPorDia[dataKey] || [];
  }

  function getPillsDoDia(dia) {
    if (!dia) return [];
    const dataKey = `${anoAtual}-${mesAtual}-${dia}`;
    return pillsPorDia[dataKey] || [];
  }

  const diasDoMes = getDiasDoMes(anoAtual, mesAtual);
  const hoje = new Date();
  const ehHoje = (dia) =>
    dia === hoje.getDate() && mesAtual === hoje.getMonth() && anoAtual === hoje.getFullYear();

  const sessoesDoDiaSelecionado = diaSelecionado
    ? getSessoesDoDia(diaSelecionado)
    : [];

  const calendarioView = (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            type="button"
            onClick={() => navegarMes(-1)}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: "1px solid #334155",
              color: "inherit",
              cursor: "pointer",
              fontSize: 16,
            }}
          >
            ◀
          </button>
          <span style={{ fontSize: 18, fontWeight: 600, minWidth: 180, textAlign: "center" }}>
            {MESES[mesAtual]} {anoAtual}
          </span>
          <button
            type="button"
            onClick={() => navegarMes(1)}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: "1px solid #334155",
              color: "inherit",
              cursor: "pointer",
              fontSize: 16,
            }}
          >
            ▶
          </button>
        </div>
        <button
          type="button"
          onClick={irParaHoje}
          className="pc-btn-secundario"
          style={{ padding: "8px 16px" }}
        >
          Hoje
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 4,
          background: "rgba(255,255,255,0.03)",
          borderRadius: 12,
          padding: 8,
        }}
      >
        {DIAS_SEMANA.map((dia) => (
          <div
            key={dia}
            style={{
              textAlign: "center",
              fontWeight: 600,
              fontSize: 13,
              padding: "8px 0",
              opacity: 0.7,
            }}
          >
            {dia}
          </div>
        ))}

        {diasDoMes.map((dia, idx) => {
          const pillsDia = getPillsDoDia(dia);
          const temSessao = pillsDia.length > 0;
          const selecionado = dia === diaSelecionado;

          return (
            <div
              key={idx}
              onClick={() => dia && setDiaSelecionado(dia)}
              style={{
                minHeight: isMobile ? 50 : 70,
                padding: 6,
                borderRadius: 8,
                background: selecionado
                  ? "rgba(59, 130, 246, 0.3)"
                  : ehHoje(dia)
                  ? "rgba(34, 197, 94, 0.15)"
                  : dia
                  ? "rgba(255,255,255,0.03)"
                  : "transparent",
                border: selecionado
                  ? "2px solid #3b82f6"
                  : ehHoje(dia)
                  ? "2px solid #22c55e"
                  : "1px solid transparent",
                cursor: dia ? "pointer" : "default",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 4,
                transition: "all 0.15s ease",
              }}
            >
              {dia && (
                <>
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: ehHoje(dia) ? 700 : 500,
                      color: ehHoje(dia) ? "#22c55e" : "inherit",
                    }}
                  >
                    {dia}
                  </span>
                  {temSessao && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "stretch",
                        width: "100%",
                        gap: 3,
                      }}
                    >
                      {pillsDia.slice(0, 3).map((pill) => (
                        <div
                          key={`${pill.tecnicoId}`}
                          style={{
                            width: "100%",
                            height: 10,
                            borderRadius: 999,
                            background: hexToRgba(pill.tecnicoCor, 0.12),
                            border: `1px solid ${hexToRgba(pill.tecnicoCor, 0.75)}`,
                            position: "relative",
                            overflow: "hidden",
                            display: "grid",
                            gridTemplateColumns: "repeat(4, 1fr)",
                            gap: 1,
                          }}
                          title={`${pill.tecnicoNome} • ${pill.totalServicos} serviço(s) • ${pill.preenchidos}/4 blocos`}
                        >
                          {[0, 1, 2, 3].map((slotIdx) => (
                            <div
                              key={`${pill.tecnicoId}-${slotIdx}`}
                              style={{
                                background: pill.slots[slotIdx]
                                  ? (pill.tecnicoCor || "#3b82f6")
                                  : "transparent",
                                borderRadius: slotIdx === 0
                                  ? "999px 0 0 999px"
                                  : slotIdx === 3
                                  ? "0 999px 999px 0"
                                  : 0,
                              }}
                            />
                          ))}
                        </div>
                      ))}
                      {pillsDia.length > 3 && (
                        <span style={{ fontSize: 10, opacity: 0.7, textAlign: "center" }}>
                          +{pillsDia.length - 3}
                        </span>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {diaSelecionado && (
        <div
          style={{
            background: "rgba(255,255,255,0.05)",
            borderRadius: 12,
            padding: 16,
            marginTop: 8,
          }}
        >
          <h4 style={{ margin: 0, marginBottom: 12 }}>
            Sessoes em {diaSelecionado}/{mesAtual + 1}/{anoAtual}
          </h4>
          {sessoesDoDiaSelecionado.length === 0 ? (
            <p style={{ opacity: 0.7, margin: 0 }}>
              Nenhuma sessao agendada para este dia.
            </p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {sessoesDoDiaSelecionado.map((s) => (
                <div
                  key={s.id}
                  style={{
                    background: hexToRgba(s.tecnicoCor, 0.12),
                    border: `1px solid ${hexToRgba(s.tecnicoCor, 0.35)}`,
                    borderRadius: 8,
                    padding: 12,
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{s.clienteNome || "Cliente"}</div>
                  <div style={{ fontSize: 13, opacity: 0.8, marginTop: 4 }}>
                    <span style={{ marginRight: 12 }}>🗓 {formatDateTime(s.inicioEm)}</span>
                    <span style={{ marginRight: 12 }}>🧑 {s.tecnicoNome || "-"}</span>
                    <span>📄 {s.atendimento?.competencia || "-"}</span>
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.6, marginTop: 4 }}>
                    Status: {s.status || "-"} | Contrato: {s.atendimento?.contratoTipo || "-"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const listaView = (
    <>
      <div style={{ marginBottom: 12 }}>
        <input
          type="text"
          value={textoFiltro}
          onChange={(e) => setTextoFiltro(e.target.value)}
          placeholder="Filtrar por cliente, tecnico, competencia..."
          style={{
            width: "100%",
            maxWidth: 420,
            padding: "8px 10px",
            borderRadius: 8,
            border: "1px solid #334155",
            color: "inherit",
          }}
        />
      </div>

      <div className="pc-table-wrapper">
        {erro && (
          <div
            style={{
              background: "#7f1d1d",
              color: "#fecaca",
              padding: 8,
              borderRadius: 8,
              marginBottom: 12,
              fontSize: 14,
            }}
          >
            {erro}
          </div>
        )}

        {carregando ? (
          <p style={{ padding: 10 }}>Carregando calendario...</p>
        ) : sessoesFiltradas.length === 0 ? (
          <p style={{ padding: 10 }}>Nenhuma sessao agendada para este periodo.</p>
        ) : (
          <table className="pc-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Cliente</th>
                <th>Tecnico</th>
                <th>Competencia</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {sessoesFiltradas.map((s) => (
                <tr key={s.id}>
                  <td>{formatDateTime(s.inicioEm)}</td>
                  <td>{s.clienteNome || "-"}</td>
                  <td>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: s.tecnicoCor || "#3b82f6",
                          display: "inline-block",
                        }}
                      />
                      {s.tecnicoNome || "-"}
                    </span>
                  </td>
                  <td>{s.atendimento?.competencia || "-"}</td>
                  <td>{s.status || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );

  const abas = (
    <div
      style={{
        display: "flex",
        gap: 8,
        marginBottom: 16,
        borderBottom: "1px solid #334155",
        paddingBottom: 8,
      }}
    >
      <button
        type="button"
        onClick={() => setAbaAtiva("calendario")}
        style={{
          padding: "8px 20px",
          borderRadius: 8,
          border: "none",
          background: abaAtiva === "calendario" ? "#3b82f6" : "transparent",
          color: abaAtiva === "calendario" ? "#fff" : "inherit",
          cursor: "pointer",
          fontWeight: abaAtiva === "calendario" ? 600 : 400,
        }}
      >
        📅 Calendario
      </button>
      <button
        type="button"
        onClick={() => setAbaAtiva("lista")}
        style={{
          padding: "8px 20px",
          borderRadius: 8,
          border: "none",
          background: abaAtiva === "lista" ? "#3b82f6" : "transparent",
          color: abaAtiva === "lista" ? "#fff" : "inherit",
          cursor: "pointer",
          fontWeight: abaAtiva === "lista" ? 600 : 400,
        }}
      >
        📋 Lista
      </button>
    </div>
  );

  const conteudo = (
    <>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 12,
          margin: "10px 0 18px",
        }}
      >
        <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13, opacity: 0.9 }}>
          Tecnico
          <select
            value={tecnicoId}
            onChange={(e) => setTecnicoId(e.target.value)}
            style={{
              padding: "8px 10px",
              borderRadius: 8,
              border: "1px solid #334155",
              background: "rgba(255,255,255,0.02)",
              color: "inherit",
            }}
          >
            <option value="">Selecione</option>
            {canGlobalView && <option value="__GLOBAL__">Global</option>}
            {tecnicos.map((t) => (
              <option key={t.id} value={t.id}>
                {t.nome || t.email || "Tecnico"}
              </option>
            ))}
          </select>
        </label>
      </div>

      {abas}

      {!tecnicoId ? (
        <p style={{ padding: 10 }}>Selecione um tecnico para visualizar o calendario.</p>
      ) : carregando ? (
        <p style={{ padding: 10 }}>Carregando...</p>
      ) : abaAtiva === "calendario" ? (
        calendarioView
      ) : (
        listaView
      )}
    </>
  );

  if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        alterarTema={alterarTema}
        titulo="Calendario"
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
      >
        <div style={{ padding: 12 }}>{conteudo}</div>
      </LayoutMobile>
    );
  }

  return (
    <LayoutDesktop tema={tema} alterarTema={alterarTema} titulo="Planejamento" subtitulo="Calendario">
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Calendario</h3>
            <p>Visualize as sessoes planejadas no calendario ou em lista.</p>
          </div>
        </header>

        {erro && <div className="pc-alert pc-alert-erro">{erro}</div>}

        {conteudo}
      </section>
    </LayoutDesktop>
  );
}
